import type { GetListParams } from "ra-core";

// Fulltekst-søk for listesøkene (SearchInput q), PR-S1 i søkeplanen.
//
// Semantikk: hvert ord i søket må treffe minst én av kolonnene — OG mellom
// ord, ELLER mellom kolonner. «Ola Nordmann» krever altså både et Ola-treff
// og et Nordmann-treff (gjerne i ulike kolonner), i stedet for gamle
// OR-semantikken som traff alle Ola-er og alle Nordmann-er.
//
// Implementasjon: bygger PostgREST sitt logiske tre direkte,
//   and=(or(kol1.ilike.*ord1*,kol2.ilike.*ord1*),or(kol1.ilike.*ord2*,…))
// via ra-data-postgrest sin rå-syntaks (nøkkel med tom operator: «and@»).
// Verdiene står ukvotert (kanonisk form, `*` blir `%`), så tegn som ville
// knekt tre-parseren (komma, parenteser, anførselstegn) strippes fra ordene.

const sanitizeWord = (word: string) => word.replace(/[,()"\\]/g, "");

export const applyFullTextSearch =
  (columns: string[]) =>
  (params: GetListParams): GetListParams => {
    if (!params.filter?.q) {
      return params;
    }
    const { q, ...filter } = params.filter;
    const words = String(q)
      .trim()
      .split(/\s+/)
      .map(sanitizeWord)
      .filter(Boolean);
    if (words.length === 0) {
      return { ...params, filter };
    }
    const groups = words.map(
      (word) =>
        `or(${columns.map((column) => `${column}.ilike.*${word}*`).join(",")})`,
    );
    return {
      ...params,
      filter: { ...filter, "and@": `(${groups.join(",")})` },
    };
  };

// Vern: q som når dataProvideren uten å ha blitt omsatt av en
// applyFullTextSearch-callback ville blitt sendt som kolonnefilter
// (?q=eq.…) til PostgREST og gitt 400 i prod (demo/fakerest maskerer det).
// Strippes her; i dev varsles utvikleren så hullet fanges lokalt.
export const stripUnhandledQ = (
  resource: string,
  params: GetListParams,
  warn: (message: string) => void = console.warn,
): GetListParams => {
  if (params.filter?.q === undefined) {
    return params;
  }
  if (import.meta.env.DEV) {
    warn(
      `Søk (q) er ikke konfigurert for ressursen «${resource}» – legg til en ` +
        `applyFullTextSearch-callback i dataProvider.ts. Søket ignoreres.`,
    );
  }
  const { q: _q, ...filter } = params.filter;
  return { ...params, filter };
};
